import { useEffect, useState } from 'react';
import { InfoBlockStatusToken } from './WorkflowInfoBlock'
import { useRemote, useStorage } from '../../Utils/Utils';
import { FileText, Info } from 'react-feather';
import { useWebsockets } from "../../Utils/WebsocketReceiverHook";
import Table from '../../components/Table/Table';
import Button from '../../components/Buttons/Button';
import AlertBlock from '../Shared/Prompts/AlertBlock';
import Divider from '../../components/Divider';
import FlexWrapper from '../FlexWrapper';
import DetailSectionHeader from '../Shared/DetailSection/DetailSectionHeader';
import SearchTermFilter from '../../components/Input/SearchTermFilter';
import Checkbox from '../../components/Checkbox';
import PdfIcon from '../../images/icons/PdfIcon';
import XlsIcon from '../../images/icons/XlsIcon';
import VideoIndicator from '../../components/Labels/VideoIndicator';
import PaginationNav from '../../components/Table/PaginationNav';
import PageContentToggle from '../../components/Menus/PageContentToggle';
import "./ActiveWorkflows.scss"

const WorkflowPublishActions = ({ workflowId, taskId }) => {
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [pagination, setPagination] = useState({ page: 1, totalPages: 1, size: 15 });
	const [files, setFiles] = useState([]);
	const [allFiles, setAllFiles] = useState([])
	const [hasSearchTerm, setHasSearchTerm] = useState(false);
	const [filteredFiles, setFilteredFiles] = useState([]);
	const [selectedFiles, setSelectedFiles] = useState([]);
	const [filteredPaginationPage, setFilteredPaginationPage] = useState(1);
	const [fileFilter, setFileFilter] = useState(null)

	const remote = useRemote()
	const storage = useStorage()
	const sockets = useWebsockets()

	const groupNames = allFiles.reduce((accumulator, file) => {
		if (accumulator.includes(file.groupName)) return accumulator
		return [...accumulator, file.groupName]
	}, [])

	const getPageOfFiles = (page = 1, data) => {
		if (!files || files.length === 0) {
			return [];
		}

		const offset = (page - 1) * pagination.size;
		return data.slice(offset, offset + pagination.size);
	}

	useEffect(() => {
		const flattenFiles = (data) => {
			return data.flatMap(item =>
				item.files.map(file => ({
					...file,
					groupName: item.groupName
				}))
			);
		};

		remote.get(`workflows/workflow/${workflowId}/workflowTask/${taskId}`).then((res) => {
			setData(res.data.data)
			setLoading(false);
			remote.get(`/workflows/list/${workflowId}/documents?num=1000&offset=0`).then(res => {
				setFiles(flattenFiles(res.data))
				setAllFiles(flattenFiles(res.data))
			})
		})
		const socketsPath = `/api/workflow/${workflowId}/workflowTask/${taskId}`
		sockets.subscribe({
			[socketsPath]: [(message) => {
				storage.refresh(`workflows/workflow/${workflowId}/workflowTask/${taskId}`)
					.then(res => {
						setData(res.data.data)
						setLoading(false);
						remote.get(`/workflows/list/${workflowId}/documents?num=1000&offset=0`).then(res => {
							setFiles(flattenFiles(res.data))
							setAllFiles(flattenFiles(res.data))
						})
					})
					.catch((err) => {
						console.error(err)
						setLoading(false)
					});
			}]
		})
		return () => {
			sockets.unsubscribe([`/api/workflow/${workflowId}/workflowTask/${taskId}`])
		}
	}, [])


	const changeFileFilter = (filter) => {
		if (filter === null) {
			setFileFilter(null)
			setFiles(allFiles)
			setSelectedFiles([])
			return
		}
		if (fileFilter === filter) {
			setFileFilter(null)
			setFiles(allFiles)
			setSelectedFiles([])
		}
		else {
			setSelectedFiles([])
			setFileFilter(filter)
			setFiles(allFiles.filter(file => file.groupName === filter))
		}
	}

	const labels = groupNames.map((group) => {
		return { label: group, active: fileFilter === group, onClick: () => changeFileFilter(group) }
	})

	labels.unshift({
		label: "All",
		active: fileFilter === null,
		onClick: () => {
			changeFileFilter(null)
		}
	})

	const handlePageChange = (newPage) => {
		hasSearchTerm
			? setFilteredPaginationPage(newPage)
			: setPagination((prev) => ({ ...prev, page: newPage }));

		setSelectedFiles([])
	};

	const visibleListOfFiles = files && hasSearchTerm ? filteredFiles : files
	const currentPageOfFiles = files && hasSearchTerm ? getPageOfFiles(filteredPaginationPage, filteredFiles) : getPageOfFiles(pagination.page, files)
	// Video files have no S3 object and can't be ZIP-downloaded — exclude them from selection.
	const selectablePageIds = currentPageOfFiles.filter(file => file.type !== 'video').map(file => file.id)
	const currentPageAllChecked = selectablePageIds.length > 0 && selectablePageIds.every((id) => selectedFiles.includes(id))

	const bulkDownload = async (downloadAll) => {
		let downloadIds = []
		if (hasSearchTerm) {
			downloadIds = downloadAll
				? visibleListOfFiles.filter((selected) => filteredFiles.some(file => file.id === selected.id)).map(file => file.id)
				: selectedFiles.filter((selected) => filteredFiles.some(file => file.id === selected))
		} else {
			downloadIds = downloadAll
				? visibleListOfFiles.map((file) => file.id)
				: selectedFiles
		}
		// Video files aren't S3 downloadable — never send them to the ZIP endpoint.
		downloadIds = downloadIds.filter((id) => {
			const f = visibleListOfFiles.find((file) => file.id === id)
			return !f || f.type !== 'video'
		})
		if (downloadIds.length === 0) {
			return
		}
		remote.post(`/workflows/documents/${workflowId}/download-zip`, downloadIds, { rawResponse: true }).then(async (res) => {

			// Check if the response is OK
			if (!res.ok) {
				throw new Error(`Failed to download files: ${res.statusText}`);
			}

			// Read the response as a stream
			const reader = res.body.getReader();
			const chunks = [];
			let done = false;

			while (!done) {
				const { value, done: streamDone } = await reader.read();
				if (value) {
					chunks.push(value); // Collect the binary chunks
				}
				done = streamDone;
			}

			// Combine all chunks into a single Blob
			const blob = new Blob(chunks, { type: 'application/zip' });
			const downloadUrl = window.URL.createObjectURL(blob);

			const a = document.createElement("a");
			a.href = downloadUrl;
			a.download = "Published Files";
			document.body.appendChild(a);
			a.click();

			document.body.removeChild(a);
			window.URL.revokeObjectURL(downloadUrl);
		}).catch((err) => {
			console.error("Error downloading the zip file:", err);
		});
	}

	const downloadSingle = async (file) => {
		// Video files have no downloadable S3 document.
		if (file && file.type === 'video') {
			return
		}
		const openInTopFrame = (url) => {
			const newWindow = window.open(url, '_top', 'noopener,noreferrer')
			if (newWindow) newWindow.opener = null
		}

		const onDocumentClick = (doc, event) => {
			if (event) {
				event.stopPropagation()
			}

			if (doc && doc.id) {
				remote.get(`/documents/document/${doc.id}`).then((response) => {
					if (response) {
						openInTopFrame(response.url);
					}
				});
			}
		}

		onDocumentClick(file)
	}

	const colWidths = ['25%', '15%', '60%']
	const headers = ['Action', 'Status', 'Info']

	function retryPublishActions() {
		setLoading(true);
		remote.post(`workflows/workflow/${workflowId}/workflowTask/${taskId}/retry`).then(() => {
			remote.get(`workflows/workflow/${workflowId}/workflowTask/${taskId}`).then((res) => {
				setData(res.data);
				setLoading(false);
			})
		})
	}

	function getErrorList(publishData) {
		if (publishData) {
			const arr = publishData.flatMap((data, idx) => {
				if (data.info && data.info.length > 0) {
					return data.info.map((e, i) => {
						return { ticker: data.ticker, string: e }
					})
				} else return null
			})
			const filtered = arr.filter(e => e !== null)
			return filtered
		} else return "-"
	}

	function dataMap(steps) {
		return steps && steps.length > 0
			? steps.map((step, idx) => {
				const { name, publishData } = step
				const isLastInList = idx === steps.length - 1
				const list = getErrorList(publishData)
				return (
					<tr key={'publish-action-step-' + name} className={`${isLastInList ? "mb-xxl" : ""}`}>
						<td>{step.publishActionName}</td>
						<td><InfoBlockStatusToken status={step.publishActionStatus} /></td>
						<td><ul>{list && list.length > 0 ? list.map((item, index) => <li className={index === list.length - 1 ? '' : 'pb-s'}><span className='font-weight-medium'>{item.ticker}:</span> {item.string} </li>) : ""}</ul></td>
					</tr>
				)
			})
			: []
	}

	const publishedFileColWidths = ["auto", { width: "200px" }]
	const PublishedFileHeaders = [
		'File Name',
		<div style={{ display: "flex", justifyContent: "end", gap: "0.5rem" }}>
			Select All
			<Checkbox
				checked={currentPageAllChecked}
				onChange={(val) => {
					currentPageAllChecked ? setSelectedFiles([]) : setSelectedFiles(selectablePageIds)
				}}
			/>
		</div>
	]
	const PublishedFileDataMap = data => data.map((file, idx) => {
		const rowIsChecked = selectedFiles.includes(file.id)
		const isVideo = file.type === 'video'
		const downloadFile = () => {
			downloadSingle(file)
		}

		return <tr key={"published-files-" + idx}>
			<td>
				<div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
					<p onClick={isVideo ? undefined : downloadFile} className={isVideo ? '' : 'workflow-published-file-name'} >
						{file.filename}
					</p>
					{isVideo && <VideoIndicator />}
				</div>
			</td>
			<td>
				<div style={{ display: "flex", justifyContent: "end", gap: "1rem", alignItems: "center" }}>
					{(() => {
                        switch (file.type) {
                            case 'xlsx':
							case 'data_file':
								return <XlsIcon className="workflow-xls-icon" onClick={downloadFile} />
                            case 'video':
                                return null
                            case 'pdf':
                                return <PdfIcon className="workflow-pdf-icon" onClick={downloadFile} />
                            default:
                                return <PdfIcon className="workflow-pdf-icon" onClick={downloadFile} />
                        }
                    })()}


					<Checkbox disabled={isVideo} checked={rowIsChecked} onChange={(val) => {
						if (rowIsChecked) {
							setSelectedFiles(selectedFiles.filter(f => f !== file.id))
						} else {
							setSelectedFiles([...selectedFiles, file.id])
						}
					}} />
				</div>
			</td>
		</tr>
	})

	return (
		<>
			{data && data.length > 0 && data.some(row => row.publishActionStatus === "Failed") &&
				<>
					<div className='flex-align-center gap-m workflow-publish-actions-warning-section mb-l' style={{ width: '100%' }}>
						<AlertBlock small icon={<Info color={"#A0001E"} />} type="danger" message="There was an error with the publish. Please try again" />
						<Button onClick={retryPublishActions} >Retry Failed Actions</Button>
					</div>
					<Divider spacingBottom="m" />
				</>
			}
			<Table
				minWidth={500}
				tableStyle={{ maxWidth: 1440 }}
				colWidths={colWidths}
				headers={headers}
				dataMap={data && dataMap(data)}
				id='worflow-publish-actions-table'
				dataLoading={loading}
			/>

			<FlexWrapper className={`mt-xl mb-l publish-files-header`} gap="m" fullWidth justify={"space-between"}>
				<FlexWrapper gap="l" >
					<DetailSectionHeader header={`Published Files (${allFiles.length})`} icon={<FileText />} />
					<SearchTermFilter
						size='small'
						width='l'
						placeholder={`Search`}
						isClearable
						dataSet={files}
						setFilteredData={(val) => { setFilteredFiles(val); setFilteredPaginationPage(1) }}
						path={['filename']}
						hasSearchTerm={(boolean) => setHasSearchTerm(boolean)}
					/>
				</FlexWrapper>
				{/* <Divider vertical height={24} /> */}
				<FlexWrapper gap="m">
					<Button onClick={() => bulkDownload(true)} >Download All ({visibleListOfFiles && visibleListOfFiles.length})</Button>
					<Button disabled={selectedFiles.length < 1} onClick={() => bulkDownload()} >Download Selected ({selectedFiles && selectedFiles.length})</Button>
				</FlexWrapper>
			</FlexWrapper>
			{/* Bounded to the table's width so a long list of offer groups scrolls instead of overflowing the page */}
			<FlexWrapper className='mb-l' fullWidth style={{ maxWidth: 1440, minWidth: 0 }}>
				<PageContentToggle style={{ overflowX: 'auto', scrollbarWidth: 'thin', whiteSpace: 'nowrap', width: '100%', paddingBottom: 4 }} labels={labels} />

			</FlexWrapper>
			<FlexWrapper direction={"column"} gap="l" className={`mb-l`} style={{ maxWidth: "1440px", paddingBottom: "6rem" }}>
				<Table
					tableStyle={{ maxWidth: 1440 }}
					minWidth={500}
					colWidths={publishedFileColWidths}
					headers={PublishedFileHeaders}
					dataMap={PublishedFileDataMap(currentPageOfFiles)}
					id='worflow-publish-actions-table'
					dataLoading={loading}
				/>
				<PaginationNav
					currentPage={hasSearchTerm ? filteredPaginationPage : pagination.page}
					totalPages={files && files.length && hasSearchTerm ? Math.ceil(filteredFiles.length / pagination.size) : Math.ceil(files.length / pagination.size)}
					onPageChange={handlePageChange}
				/>
			</FlexWrapper>
		</>
	)
}

export default WorkflowPublishActions